import type { TraceState } from "@/store/trace/state";
import type { RootState } from "@/store/types";
import type { GotTraceSpans, Span } from "@/models/traces";
import apolloClient from "@/apollo";
import type { ActionContext } from "vuex";
import { gql } from "graphql-tag";

const getTraceSpansQuery = gql`
  query GetTraceSpans($input: GetTraceSpans!) {
    getTraceSpans(input: $input) {
      __typename
      pageInfo {
        endCursor
        hasNextPage
      }
      spans {
        id
        name
        label
        starts
        ends
        elapsed
        language
        libraryName
        libraryVersion
        serviceInstanceId
        serviceName
        serviceNamespace
        serviceVersion
        statusCode
        statusMessage
        attributes {
          key
          value
        }
        events {
          attributes {
            key
            value
          }
          name
          timestamp
        }
        resourceAttributes {
          key
          value
        }
      }
    }
  }
`;

/**
 * Function in charge of fetching the Spans of the given Trace.
 * @param context - Context where the action is being taking plase.
 * @param traceId - Trace Id to be send as parameter to the query getTraceSpans.
 * @param filename - String with the encoded filename where the Spans will be searched.
 * @param cursor - PageCursor string value used to paginate results.
 * @returns Promise of the query call | Error object
 */
export default async function getTraceSpans(
  context: ActionContext<TraceState, RootState>,
  traceId: string,
  filename: string,
  cursor?: string | undefined
): Promise<undefined | { message: string; traceId: string }> {
  try {
    const result = await apolloClient.query({
      query: getTraceSpansQuery,
      variables: {
        input: {
          traceId: traceId,
          filename: filename,
          pageCursor: cursor,
        },
      },
      fetchPolicy: "no-cache",
    });
    const gotTraceSpans: GotTraceSpans = result.data.getTraceSpans;

    const spans = (context.getters["getSpans"] || []).concat(
      gotTraceSpans.spans
    ) as Span[];
    spans.sort((a, b) => {
      const first = a.starts ? a.starts : 0;
      const second = b.starts ? b.starts : 0;
      return first - second;
    });
    context.commit("setSpans", spans);

    const pageInfo = gotTraceSpans.pageInfo;
    if (pageInfo.hasNextPage)
      await getTraceSpans(context, traceId, filename, pageInfo.endCursor);
  } catch (e: any) {
    const error = e.graphQLErrors[0];
    console.error(error);
    return {
      message: error.message,
      traceId: traceId,
    };
  }
}
